import {
    POSConfig,
    PosProjectRow,
    getTemplateMissingKeys,
} from "./PosConfigurationModal/constants";

export interface PublishEligibilityResult {
    isValid: boolean;
    errors: string[];
}

type Translate = (key: string) => string;

function getTemplateMissing(
    template: POSConfig | Record<string, unknown>,
    t: Translate
): string[] {
    return getTemplateMissingKeys(template).map((key) => t(key));
}

function isEmptyTemplate(
    template: PosProjectRow["template"] | Record<string, unknown> | undefined
): boolean {
    return (
        !template ||
        typeof template !== "object" ||
        Object.keys(template).length === 0
    );
}

function resolveDisplayName(
    row: PosProjectRow,
    t: Translate,
    fallback?: string
): string {
    return (
        (row[t("Designation")] as string) ||
        row.Designation ||
        fallback ||
        ""
    );
}

/**
 * Pure validator for POS template publish eligibility. Walks the
 * franchise -> shops -> apps tree and returns the list of human-readable
 * error lines (already translated) so the caller can display them.
 *
 * isValid is true when every level (franchise, each shop with at least one
 * app, each app) has a non-empty template with the minimum required
 * categories active.
 */
export function validatePublishEligibility(
    row: PosProjectRow,
    subRows: PosProjectRow[],
    t: Translate
): PublishEligibilityResult {
    const errors: string[] = [];
    const franchiseLabel = resolveDisplayName(row, t, t("Franchise"));

    if (isEmptyTemplate(row.template)) {
        errors.push(`${franchiseLabel}: ${t("not configured")}`);
    } else {
        const franchiseMissing = getTemplateMissing(row.template!, t);
        if (franchiseMissing.length > 0) {
            errors.push(`${franchiseLabel}: ${franchiseMissing.join(", ")}`);
        }
    }

    for (const shop of subRows) {
        const shopLabel = resolveDisplayName(shop, t, shop.id_boutique);
        const shopApps = shop.subRows || [];
        const shopIssues: string[] = [];
        const appIssues: string[] = [];

        if (shopApps.length === 0) {
            shopIssues.push(t("no application associated"));
        }

        if (isEmptyTemplate(shop.template)) {
            shopIssues.push(t("not configured"));
        } else {
            const shopMissing = getTemplateMissing(shop.template!, t);
            if (shopMissing.length > 0) {
                shopIssues.push(shopMissing.join(", "));
            }
        }

        for (const app of shopApps) {
            const appLabel = resolveDisplayName(app, t, app.appId);
            if (isEmptyTemplate(app.template)) {
                appIssues.push(`  → ${appLabel}: ${t("not configured")}`);
            } else {
                const appMissing = getTemplateMissing(app.template!, t);
                if (appMissing.length > 0) {
                    appIssues.push(
                        `  → ${appLabel}: ${appMissing.join(", ")}`
                    );
                }
            }
        }

        if (shopIssues.length > 0 || appIssues.length > 0) {
            const shopPart =
                shopIssues.length > 0 ? `: ${shopIssues.join(", ")}` : "";
            const appPart =
                appIssues.length > 0 ? `\n${appIssues.join("\n")}` : "";
            errors.push(`${shopLabel}${shopPart}${appPart}`);
        }
    }

    return { isValid: errors.length === 0, errors };
}
